import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname, parseError } from "../../../utils";
import LoadingButton from "../../../components/LoadingButton";

interface InboxTransferDialogProps {
  open: boolean;
  onClose: () => void;
  inbox: any;
  companyId: string;
  getToken: () => Promise<string>;
  onTransferComplete: () => void;
}

const InboxTransferDialog: React.FC<InboxTransferDialogProps> = ({
  open,
  onClose,
  inbox,
  companyId,
  getToken,
  onTransferComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (open && companyId) {
      loadTeamMembers();
    }
  }, [open, companyId]);

  const loadTeamMembers = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/company_users/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Filter out the current owner
      const members = response.data.company_users.filter(
        (u: any) => u.uid !== inbox.uid
      );
      setTeamMembers(members);
    } catch (err) {
      console.error("Failed to load team members:", err);
      setError("Failed to load team members");
    }
  };

  const handleTransfer = async () => {
    if (!selectedEmail) {
      setError("Please select a team member to transfer ownership to");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/inboxes/${inbox.id}/transfer`,
        {
          companyId,
          newOwnerEmail: selectedEmail,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      onTransferComplete();
      onClose();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedEmail("");
      setError("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Transfer Inbox Ownership</Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              You are about to transfer ownership of the inbox "
              <strong>{inbox?.name}</strong>" to another team member. Once
              transferred, you will no longer have direct access to this inbox
              unless it is set to "Shared".
            </Typography>
          </Alert>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Current owner: <strong>{inbox?.ownerEmail || "You"}</strong>
          </Typography>

          <Autocomplete
            options={teamMembers}
            getOptionLabel={(option) => option.email || ""}
            value={teamMembers.find((m) => m.email === selectedEmail) || null}
            onChange={(_, newValue) =>
              setSelectedEmail(newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select new owner"
                placeholder="Choose a team member"
                fullWidth
                helperText="The selected team member will become the new owner of this inbox"
              />
            )}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 3 }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <LoadingButton
              onClick={handleTransfer}
              variant="contained"
              color="primary"
              loading={loading}
              disabled={!selectedEmail}
            >
              Transfer Ownership
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InboxTransferDialog;